import type { Locale } from '../i18n/messages.ts';

/**
 * Long-form pages: the chess guide, the privacy policy and the about page.
 * Kept out of messages.ts because they are articles, not interface strings.
 * "{email}" in a paragraph becomes a mailto link to CONTACT_EMAIL.
 */

export const CONTACT_EMAIL = 'info@chessrokko.com';

export type InfoPageId = 'guide' | 'privacy' | 'about';

export interface InfoSection {
  heading: string;
  paragraphs?: string[];
  list?: string[];
}

export interface InfoPageText {
  emoji: string;
  title: string;
  intro: string;
  /** Shown under the title (the privacy policy's last update). */
  note?: string;
  sections: InfoSection[];
}

export const INFO_PATHS: Record<InfoPageId, string> = {
  guide: '/how-to-play',
  privacy: '/privacy',
  about: '/about',
};

export const INFO_PAGES: Record<InfoPageId, Record<Locale, InfoPageText>> = {
  guide: {
    tr: {
      emoji: '📘',
      title: 'Satranç nasıl oynanır?',
      intro:
        'Satranç iki kişiyle, 64 kareli bir tahtada oynanır. Her oyuncunun 16 taşı vardır: bir şah, bir vezir, iki kale, iki fil, iki at ve sekiz piyon. Amaç rakibin şahını kaçamayacağı şekilde tehdit etmek, yani mat etmektir. Bu rehber kuralları adım adım anlatıyor; okuduktan sonra hemen bir oyun açıp deneyebilirsin.',
      sections: [
        {
          heading: 'Tahtayı kurmak',
          paragraphs: [
            'Tahta, her oyuncunun sağ alt köşesinde beyaz bir kare olacak şekilde konur. Taşlar ilk iki sıraya dizilir: arka sırada köşelerde kaleler, yanlarında atlar, onların yanında filler bulunur. Vezir kendi renginde kareye (beyaz vezir beyaz kareye, siyah vezir siyah kareye) konur, şah da yanına. Ön sıranın tamamı piyonlardır.',
            'Oyuna her zaman beyaz başlar ve oyuncular sırayla birer hamle yapar. Chessrokko’da tahta kendiliğinden kurulur; sen sadece rengini seçersin.',
          ],
        },
        {
          heading: 'Taşlar nasıl hareket eder?',
          list: [
            'Şah (♚): Her yöne bir kare gider. En değerli taştır; kaybedilirse oyun biter, bu yüzden onu korumak önemlidir.',
            'Vezir (♛): Düz ve çapraz, istediği kadar kare gider. Tahtanın en güçlü taşıdır.',
            'Kale (♜): Yalnızca düz (yatay ve dikey) istediği kadar kare gider.',
            'Fil (♝): Yalnızca çapraz istediği kadar kare gider. Bu yüzden her fil oyun boyunca hep aynı renk karelerde kalır.',
            'At (♞): "L" şeklinde gider: iki kare düz, sonra bir kare yana. Diğer taşların üzerinden atlayabilen tek taştır.',
            'Piyon (♟): Bir kare ileri gider; ilk hamlesinde iki kare de gidebilir. Rakip taşı ise yalnızca çaprazda bir kare ileride yer. Geri gidemez.',
          ],
          paragraphs: [
            'Bir taş, rakibin taşının bulunduğu kareye giderek onu yer ve tahtadan çıkarır. Kendi taşlarının üzerine gidemezsin, at dışındaki taşlar da başka taşların üzerinden atlayamaz.',
          ],
        },
        {
          heading: 'Özel hamleler',
          list: [
            'Rok: Şah iki kare kaleye doğru gider ve kale şahın öbür yanına geçer. Bunun için şah ve o kale daha önce hiç oynamamış, aralarındaki kareler boş olmalı. Şah şah altındayken, geçtiği karede ya da gideceği karede tehdit varken rok yapılamaz.',
            'Terfi: Tahtanın öbür ucuna ulaşan piyon vezir, kale, fil ya da ata dönüşür. Çoğu zaman vezir seçilir. Ayarlardan "Piyonu her zaman vezire terfi ettir" seçeneğini açabilirsin.',
            'Geçerken alma: Bir piyon ilk hamlesinde iki kare gidip rakip piyonun hemen yanına gelirse, rakip piyon onu tek kare gitmiş gibi çaprazdan alabilir. Bu hak yalnızca hemen bir sonraki hamlede kullanılabilir.',
          ],
        },
        {
          heading: 'Şah, mat ve pat',
          paragraphs: [
            'Şah tehdit altındaysa buna "şah" denir. Şah çeken oyuncunun rakibi bir sonraki hamlede şahını kurtarmak zorundadır: şahı kaçırabilir, tehdit eden taşı yiyebilir ya da araya bir taş koyabilir.',
            'Şahı kurtarmanın hiçbir yolu yoksa bu "mat"tır ve oyun biter; mat eden oyuncu kazanır.',
            'Sırası gelen oyuncu şah altında değilse ama yapabileceği hiçbir kurallı hamle yoksa buna "pat" denir. Pat beraberliktir; öndeyken dikkatsizce pat yapmamaya dikkat et.',
          ],
        },
        {
          heading: 'Beraberlik',
          list: [
            'Pat: Yukarıda anlatıldığı gibi hamle yapamayan taraf.',
            'Yetersiz materyal: İki tarafta da mat etmeye yetecek taş kalmadıysa (örneğin yalnız şaha karşı şah ve fil).',
            'Üç kez tekrar: Aynı pozisyon üç kez oluşursa.',
            'Elli hamle kuralı: Elli hamle boyunca hiç taş alınmaz ve piyon oynanmazsa.',
          ],
        },
        {
          heading: 'Yeni başlayanlar için ipuçları',
          list: [
            'Merkezi kontrol et: İlk hamlelerde piyonlarınla ve atlarınla tahtanın ortasındaki dört kareye yerleş.',
            'Taşlarını geliştir: Atlarını ve fillerini erkenden oyuna sok, aynı taşı üst üste oynama.',
            'Şahını güvenceye al: Rok yaparak şahını kenara, piyonlarının arkasına sakla.',
            'Her hamleden önce sor: "Rakibim ne tehdit ediyor? Benim taşım korumasız mı?"',
            'Taşların değerini bil: Piyon 1, at ve fil 3, kale 5, vezir 9 puan gibi düşünülür. Değerli bir taşı daha az değerli birine karşı verme.',
          ],
        },
        {
          heading: 'Chessrokko’da pratik yap',
          paragraphs: [
            'Bilgisayara karşı oyunda Tırtıl’dan Ejderha’ya sekiz seviye var. Önce kolay rakiplerle başla, kazandıkça bir üst seviyeye geç. Takıldığında "İpucu" düğmesi iyi bir hamle gösterir, "Geri al" ile hatanı düzeltip yeniden deneyebilirsin.',
            'Hazır hissettiğinde online oyunda bir rakiple eşleş ya da arkadaşına bir bağlantı gönderip birlikte oyna. İyi oyunlar!',
          ],
        },
      ],
    },
    en: {
      emoji: '📘',
      title: 'How to play chess',
      intro:
        'Chess is played by two players on a board of 64 squares. Each player has 16 pieces: a king, a queen, two rooks, two bishops, two knights and eight pawns. The goal is to attack the opponent’s king so that it cannot escape: checkmate. This guide explains the rules step by step, so you can start a game right after reading it.',
      sections: [
        {
          heading: 'Setting up the board',
          paragraphs: [
            'The board is placed so that each player has a light square in the bottom-right corner. The pieces fill the first two rows: rooks in the corners, knights next to them, then bishops. The queen goes on her own colour (white queen on a light square, black queen on a dark one) and the king beside her. The front row is all pawns.',
            'White always moves first, and the players take turns making one move each. On Chessrokko the board is set up for you; you only choose your colour.',
          ],
        },
        {
          heading: 'How the pieces move',
          list: [
            'King (♚): One square in any direction. It is the most important piece; if it is lost the game is over, so keep it safe.',
            'Queen (♛): Any number of squares straight or diagonally. The strongest piece on the board.',
            'Rook (♜): Any number of squares straight (across or up and down).',
            'Bishop (♝): Any number of squares diagonally, so each bishop stays on one colour for the whole game.',
            'Knight (♞): Moves in an "L": two squares straight, then one to the side. The only piece that can jump over others.',
            'Pawn (♟): One square forward, or two on its very first move. It captures one square diagonally forward and can never move back.',
          ],
          paragraphs: [
            'A piece captures by moving onto the square of an enemy piece, which is then removed. You cannot move onto your own pieces, and apart from the knight no piece can jump over others.',
          ],
        },
        {
          heading: 'Special moves',
          list: [
            'Castling: The king moves two squares towards a rook and the rook jumps to the king’s other side. Neither piece may have moved before, the squares between them must be empty, and the king may not be in check, pass through an attacked square or land on one.',
            'Promotion: A pawn that reaches the far end of the board becomes a queen, rook, bishop or knight. Most players choose a queen; you can turn on "Always promote to queen" in Settings.',
            'En passant: If a pawn moves two squares on its first move and lands right beside an enemy pawn, that pawn may capture it as if it had moved only one square. This is only allowed on the very next move.',
          ],
        },
        {
          heading: 'Check, checkmate and stalemate',
          paragraphs: [
            'When a king is attacked it is in "check". The player in check must get out of it on the next move: move the king, capture the attacking piece, or block the attack.',
            'If there is no way to escape, it is "checkmate" and the game ends; the player who gave checkmate wins.',
            'If the player to move is not in check but has no legal move, it is "stalemate", which is a draw. When you are winning, take care not to stalemate your opponent by accident.',
          ],
        },
        {
          heading: 'Draws',
          list: [
            'Stalemate: the side to move has no legal move, as described above.',
            'Insufficient material: neither side has enough pieces left to checkmate (for example king and bishop against a lone king).',
            'Threefold repetition: the same position appears three times.',
            'Fifty-move rule: fifty moves pass with no capture and no pawn move.',
          ],
        },
        {
          heading: 'Tips for beginners',
          list: [
            'Control the centre: in the first moves, use pawns and knights to take the four squares in the middle.',
            'Develop your pieces: bring knights and bishops out early and avoid moving the same piece again and again.',
            'Keep your king safe: castle to tuck your king away behind its pawns.',
            'Before every move, ask: "What is my opponent threatening? Is any of my pieces unprotected?"',
            'Know what pieces are worth: think of a pawn as 1, a knight or bishop as 3, a rook as 5 and a queen as 9. Don’t give away a strong piece for a weaker one.',
          ],
        },
        {
          heading: 'Practise on Chessrokko',
          paragraphs: [
            'Playing the computer, you can choose from eight levels, from Caterpillar to Dragon. Start with an easy opponent and move up as you win. When you are stuck, the Hint button shows a good move, and Undo lets you take back a mistake and try again.',
            'When you feel ready, play online, or send a friend a link and play together. Have fun!',
          ],
        },
      ],
    },
  },

  privacy: {
    tr: {
      emoji: '🔒',
      title: 'Gizlilik politikası',
      intro:
        'Chessrokko her yaştan oyuncu için, çocuklar da düşünülerek yapıldı. Bu yüzden olabildiğince az bilgi topluyoruz. Bu sayfa neyi neden sakladığımızı ve hangi hizmetlerle çalıştığımızı sade bir dille anlatıyor.',
      note: 'Son güncelleme: 3 Kasım 2026',
      sections: [
        {
          heading: 'Giriş yapmadan oynamak',
          paragraphs: [
            'Bilgisayara karşı, arkadaşınla ya da online oynamak için hesap açman gerekmez. Misafir olarak oynarken senden hiçbir kişisel bilgi istemiyoruz; sana rastgele bir misafir adı verilir.',
          ],
        },
        {
          heading: 'Hesap açınca sakladıklarımız',
          paragraphs: [
            'Giriş "Google ile giriş" üzerinden yapılır ve Google’dan yalnızca "openid" iznini isteriz. Bu sayede Google bize sadece hesabına ait anonim bir kimlik numarası verir. E-posta adresini, gerçek adını ya da fotoğrafını almıyor ve saklamıyoruz.',
            'Hesabınla birlikte şunları saklarız:',
          ],
          list: [
            'Seçtiğin kullanıcı adı ve avatar, çerçeve, isim süsü gibi görünüm tercihlerin',
            'Puanın, oyun sayıların (galibiyet, mağlubiyet, beraberlik) ve puanlı oyunların sonuçları',
            'Altın bakiyen, altın hareketleri, sahip olduğun eşyalar, rozetlerin ve hediye kutuların',
            'Eşya açmak için izlediğin ödüllü reklamların sayısı',
            'Oturum bilgisi: tarayıcında 30 gün geçerli bir oturum çerezi tutulur; sunucuda bu çerezin yalnızca şifrelenmiş (hash) hâli bulunur',
          ],
        },
        {
          heading: 'Herkese açık bilgiler',
          paragraphs: [
            'Kullanıcı adın, görünümün, puanın, oyun istatistiklerin ve rozetlerin profil sayfanda ve liderlik tablolarında herkese görünür. Bu yüzden kullanıcı adında gerçek adını ya da kişisel bir bilgiyi kullanmamanı öneririz.',
          ],
        },
        {
          heading: 'Cihazında tutulan bilgiler',
          list: [
            'Tercihlerin (dil, tema, tahta rengi, ses ayarları) ve bitmemiş bilgisayar oyunun tarayıcının yerel depolamasında (localStorage) tutulur ve sunucumuza gönderilmez.',
            'Dil seçimini hatırlamak için bir yıl geçerli "lang" çerezi kullanılır.',
            'Giriş yaptıysan "oc_session" oturum çerezi kullanılır.',
          ],
        },
        {
          heading: 'Oyunlar ve teknik bilgiler',
          paragraphs: [
            'Online ve arkadaşla oynanan oyunlarda hamleler, oyunu yürütebilmek için sunucumuza gönderilir. Bilgisayara karşı oyunda satranç motoru (Stockfish) tamamen senin tarayıcında çalışır.',
            'Site Cloudflare altyapısında çalışır. Cloudflare, siteyi sana ulaştırmak ve kötüye kullanımı önlemek için (örneğin çok sayıda oda açılmasını sınırlamak) IP adresini işler. IP adresini kendi veritabanımızda saklamıyoruz.',
          ],
        },
        {
          heading: 'Ziyaret istatistikleri',
          paragraphs: [
            'Kaç kişinin siteyi ziyaret ettiğini görmek için Cloudflare Web Analytics kullanıyoruz. Bu araç çerez kullanmaz, seni siteler arasında takip etmez ve kişisel profil oluşturmaz.',
          ],
        },
        {
          heading: 'Reklamlar',
          paragraphs: [
            'Chessrokko ücretsizdir. Masrafları karşılamak için markette isteğe bağlı ödüllü video reklamlar bulunur: reklam yalnızca sen "İzle" düğmesine bastığında gösterilir ve karşılığında altın ya da eşya kazanırsın. Oyun sırasında reklam çıkmaz.',
            'Reklamları Google AdSense sağlar. Tüm reklam isteklerini "çocuklara yönelik" olarak işaretliyoruz; bu durumda Google kişiselleştirilmiş reklam göstermez ve çocuklara uygun reklam sunar. Google, reklamı göstermek, sıklığını sınırlamak ve sahtekârlığı önlemek için çerezler veya benzeri teknolojiler kullanabilir. Ayrıntılar için Google’ın "Google, iş ortaklarımızın sitelerini veya uygulamalarını kullandığınızda verileri nasıl kullanır" sayfasına bakabilirsin: policies.google.com/technologies/partner-sites',
            'Altın hiçbir zaman gerçek parayla satılmaz.',
          ],
        },
        {
          heading: 'Çocuklar',
          paragraphs: [
            'Siteyi çocukların da güvenle kullanabilmesi için e-posta, gerçek ad, fotoğraf, konum ya da iletişim bilgisi toplamıyoruz ve kişiselleştirilmiş reklam göstermiyoruz. Ebeveynler, çocuklarının hesabıyla ilgili her türlü soru ya da silme talebi için bize yazabilir.',
          ],
        },
        {
          heading: 'Bilgilerin paylaşılması',
          paragraphs: [
            'Bilgilerini satmıyoruz. Bilgiler yalnızca siteyi çalıştırmak için kullandığımız hizmet sağlayıcılarla (Cloudflare barındırma ve veritabanı, Google giriş ve reklam) ve yasal bir zorunluluk olduğunda paylaşılır.',
          ],
        },
        {
          heading: 'Haklarını kullanmak',
          paragraphs: [
            'KVKK ve GDPR gibi veri koruma yasaları kapsamında hesabınla ilgili bilgileri öğrenme, düzeltme ve silme hakkın var. Hesabının ve tüm bilgilerinin silinmesini istersen kullanıcı adınla birlikte {email} adresine yazman yeterli. Hesabını sildiğimizde profilin, puanın ve eşyaların kalıcı olarak kaldırılır.',
          ],
        },
        {
          heading: 'Değişiklikler ve iletişim',
          paragraphs: [
            'Bu politikayı güncellersek bu sayfadaki tarihi değiştiririz. Önemli değişiklikleri sitede duyururuz. Soruların için: {email}',
          ],
        },
      ],
    },
    en: {
      emoji: '🔒',
      title: 'Privacy policy',
      intro:
        'Chessrokko is made for players of all ages, with children in mind, so we collect as little as we can. This page explains in plain words what we keep, why, and which services we work with.',
      note: 'Last updated: 3 November 2026',
      sections: [
        {
          heading: 'Playing without an account',
          paragraphs: [
            'You don’t need an account to play the computer, a friend or online. As a guest we ask for no personal information; you get a random guest name.',
          ],
        },
        {
          heading: 'What we keep when you sign in',
          paragraphs: [
            'Sign-in uses "Sign in with Google", and we only ask Google for the "openid" permission. Google then gives us nothing but an anonymous ID for your account. We do not receive or store your email address, real name or photo.',
            'With your account we keep:',
          ],
          list: [
            'The username you choose and your look (avatar, frame, name style)',
            'Your rating, game counts (wins, losses, draws) and the results of rated games',
            'Your gold balance and history, the items you own, your badges and gift boxes',
            'How many rewarded ads you watched towards unlocking items',
            'Sign-in session: a session cookie valid for 30 days in your browser; the server only keeps a hashed copy of it',
          ],
        },
        {
          heading: 'What is public',
          paragraphs: [
            'Your username, look, rating, game statistics and badges appear on your profile page and on the leaderboards for everyone to see. That is why we suggest you don’t use your real name or other personal details in your username.',
          ],
        },
        {
          heading: 'What is kept on your device',
          list: [
            'Your settings (language, theme, board colours, sound) and an unfinished game against the computer are kept in your browser’s local storage and are not sent to our server.',
            'A "lang" cookie, valid for one year, remembers your language choice.',
            'When you are signed in, the "oc_session" cookie keeps you signed in.',
          ],
        },
        {
          heading: 'Games and technical data',
          paragraphs: [
            'In online games and games with a friend, moves are sent to our server so the game can run. When you play the computer, the chess engine (Stockfish) runs entirely in your browser.',
            'The site runs on Cloudflare. Cloudflare processes your IP address to deliver the site and to prevent abuse (for example, limiting how many rooms can be created). We do not store IP addresses in our own database.',
          ],
        },
        {
          heading: 'Visitor statistics',
          paragraphs: [
            'We use Cloudflare Web Analytics to count visits. It uses no cookies, does not follow you across sites and builds no personal profile.',
          ],
        },
        {
          heading: 'Ads',
          paragraphs: [
            'Chessrokko is free. To cover its costs, the shop offers optional rewarded video ads: an ad only plays when you press "Watch", and you earn gold or items in return. No ads appear during games.',
            'Ads are provided by Google AdSense. Every ad request is marked as child-directed, so Google shows no personalised ads and serves child-appropriate ads. Google may use cookies or similar technologies to show ads, limit how often they appear and prevent fraud. For details, see Google’s page "How Google uses information from sites or apps that use our services": policies.google.com/technologies/partner-sites',
            'Gold is never sold for real money.',
          ],
        },
        {
          heading: 'Children',
          paragraphs: [
            'So that children can use the site safely, we collect no email, real name, photo, location or contact details, and we show no personalised ads. Parents can write to us with any question about their child’s account or to have it deleted.',
          ],
        },
        {
          heading: 'Sharing',
          paragraphs: [
            'We do not sell your information. It is shared only with the service providers that run the site (Cloudflare for hosting and the database, Google for sign-in and ads) and when the law requires it.',
          ],
        },
        {
          heading: 'Your rights',
          paragraphs: [
            'Under data protection laws such as GDPR and Turkey’s KVKK, you can ask to see, correct or delete the information linked to your account. To have your account and all its data deleted, write to {email} with your username. Once deleted, your profile, rating and items are removed for good.',
          ],
        },
        {
          heading: 'Changes and contact',
          paragraphs: ['If we update this policy we change the date on this page and announce important changes on the site. Questions: {email}'],
        },
      ],
    },
  },

  about: {
    tr: {
      emoji: '♞',
      title: 'Hakkında',
      intro:
        'Chessrokko, satrancı her yaştan insan için kolay, eğlenceli ve güvenli hâle getirmek için yapılmış ücretsiz bir online satranç sitesidir. Büyük düğmeleri, sevimli rakipleri ve sade arayüzüyle özellikle satranca yeni başlayan çocuklar düşünülerek tasarlandı.',
      sections: [
        {
          heading: 'Neler yapabilirsin?',
          list: [
            'Online oyna: Saniyeler içinde bir rakiple eşleş, giriş yaparsan puan kazan ve liderlik tablosunda yüksel.',
            'Bilgisayarla oyna: Acemiden ustaya sekiz seviyeli rakiplerle pratik yap; ipucu al, hamleni geri al.',
            'Arkadaşınla oyna: Bir oda oluştur, bağlantıyı gönder ve hemen birlikte oyna. Üyelik gerekmez.',
            'Topla ve kişiselleştir: Oyunlarda altın kazan, marketten avatar, çerçeve ve isim süsleri al, rozetler topla.',
          ],
        },
        {
          heading: 'Ücretsiz ve güvenli',
          paragraphs: [
            'Chessrokko’nun tüm oyun özellikleri ücretsizdir. Altın gerçek parayla satılmaz; yalnızca oynayarak ya da isteğe bağlı ödüllü reklam izleyerek kazanılır. Oyun sırasında reklam gösterilmez.',
            'Çocukların güvenliği için e-posta ya da gerçek ad gibi kişisel bilgiler toplamıyoruz ve sohbet özelliği bulunmuyor. Ayrıntılar gizlilik politikasında.',
          ],
        },
        {
          heading: 'Teşekkürler',
          paragraphs: [
            'Bilgisayar rakibi, açık kaynaklı satranç motoru Stockfish (GPLv3) ile çalışır. Taş hareketlerinin doğruluğu için chess.js kütüphanesini kullanıyoruz.',
          ],
        },
        {
          heading: 'İletişim',
          paragraphs: ['Öneri, hata bildirimi ya da sorular için bize yazabilirsin: {email}'],
        },
      ],
    },
    en: {
      emoji: '♞',
      title: 'About',
      intro:
        'Chessrokko is a free online chess site made to keep chess easy, fun and safe for people of all ages. With big buttons, friendly opponents and a simple interface, it is designed especially for children who are just starting out.',
      sections: [
        {
          heading: 'What you can do',
          list: [
            'Play online: get matched with an opponent in seconds; sign in to earn rating points and climb the leaderboard.',
            'Play the computer: practise against eight levels, from beginner to master, with hints and take-backs.',
            'Play a friend: create a room, send the link and play together right away. No sign-up needed.',
            'Collect and customise: win gold in games, get avatars, frames and name styles in the shop, and collect badges.',
          ],
        },
        {
          heading: 'Free and safe',
          paragraphs: [
            'Every game feature on Chessrokko is free. Gold is never sold for money; it is earned only by playing or by watching optional rewarded ads. No ads appear during games.',
            'To keep children safe we collect no personal details such as email or real name, and there is no chat. See the privacy policy for details.',
          ],
        },
        {
          heading: 'Thanks',
          paragraphs: [
            'The computer opponent is powered by the open-source chess engine Stockfish (GPLv3). We use the chess.js library to make sure every move follows the rules.',
          ],
        },
        {
          heading: 'Contact',
          paragraphs: ['Ideas, bug reports or questions? Write to us: {email}'],
        },
      ],
    },
  },
};
